import type { AiCapabilityPage } from "../schema.js";
import { ALL_ROLES, LIVE, WRITE_FLAGS, SLOT_COLLECTION_AUTOFILL, SLOT_GROUP } from "./_shared.js";

/**
 * `PAY-REPORT` (@UC0197) — TỰ BÁO đã chuyển cho suất của CHÍNH MÌNH, T2. Executor = `ReportPaymentService.reportPayment`
 * (collections/report-payment.service.ts:524) — cùng cửa với nút "Tôi đã chuyển" trên mobile.
 *
 * 🔴🔴 **TRANG DUY NHẤT TỚI GIỜ MÀ CÙNG MỘT LƯỢT CHO HAI KẾT QUẢ TIỀN KHÁC NHAU.** Đây là thứ phải đọc trước khi sửa
 * bất cứ chữ nào ở dưới. `reportPayment` quyết bằng **hai đường ĐỘC LẬP** (`report-payment.service.ts:875`):
 *
 *     autoConfirm = actorCanSelfApprove(vai người khai) || resolveSelfPaymentAutoConfirm(quỹ)
 *
 * `||` chứ không `&&`, và đó là QĐ người dùng 31/08/2026 (`self-payment-approver.ts`): bắt thủ quỹ chờ duyệt chính
 * mình là vòng luẩn quẩn — nhóm một thủ quỹ thì khoản họ đóng nằm chờ vĩnh viễn. Hệ quả cho trang:
 *
 *   | Người khai             | Cờ quỹ TẮT (mặc định) | Cờ quỹ BẬT |
 *   |------------------------|-----------------------|------------|
 *   | member thường          | §3.7 (2) — 0 bút toán | vào sổ NGAY |
 *   | O/A/T (có quyền duyệt) | **vào sổ NGAY**       | vào sổ NGAY |
 *
 * ⇒ **Câu chữ về hệ quả nằm trong GIÁ TRỊ của fact `ketQua`, KHÔNG trong template.** Template là chữ TĨNH; bản `draft`
 * trước đây nói cứng *"Thủ quỹ sẽ đối chiếu rồi mới vào sổ"* và câu đó **sai ở ba trong bốn ô của bảng trên** — sai
 * ngay trên THẺ XÁC NHẬN, tức AI-INV-4 (*thẻ phải mô tả ĐÚNG hệ quả*). Đây đúng khuôn `duGhi`/`duLabel` của
 * `RECORD-PAID` (#853 vòng 2 M2): hệ quả khác nhau theo cấu hình mà template tĩnh ⇒ câu chữ đi vào fact.
 * `pay-report.page.test.ts` khoá cả hai chiều: fact có hai giá trị cho hai chữ khác nhau, VÀ template tĩnh không
 * được chứa lời hứa "đối chiếu" nào.
 *
 * 🔴 **`userIsLinkedToSeat` KHÔNG nới** (`CLAUDE.md §3.7`). Nới 27/08 nói *lời khai có cần người thứ hai không*; nó
 * KHÔNG nói *khai hộ ai cũng được*. Chốt "chỉ khai cho suất mình đại diện" là chốt chặn quan trọng nhất còn lại sau
 * khi bỏ bước xác nhận — thiếu nó thì một lời khai bơm được số dư của suất BẤT KỲ. Binding lấy hàng qua
 * `listSelfReportableDuesForUser` (chính cổng ghế đó), và `reportPayment` kiểm lại lần nữa lúc ghi ⇒ 403.
 *
 * 🔴 **Ô tuỳ chọn KHÔNG được làm chỗ trống của phiếu.** `renderTemplate` NÉM khi chỗ trống rỗng, nên `{soTien}`/`{ghiChu}`
 * trong `confirm` là phiếu vỡ ngay lượt người dùng không nói số / không ghi chú. Số lên phiếu là fact `soKhai`
 * (số executor SẼ khai — #811 M1), chữ lên phiếu là fact `ghiChuGhi` — nằm trong ĐOẠN TUỲ CHỌN `[[…]]` (US-AIV2-0114 L05): rỗng ⇒ cả vế
 * ghi chú và dòng phiếu của nó biến mất (trước: chữ giả "(không có)" cho đủ chỗ trống — một dòng thừa trên phiếu tiền).
 *
 * 🔴 **`ghiChuGhi` là AI-INV-4, không phải trang trí.** `note` là field DUY NHẤT của lời khai do người dùng gõ, và nó
 * là CHỮ DO LLM TRÍCH. Chữ đó đi vào bản ghi thủ quỹ đọc lúc đối chiếu. Không in lên phiếu = mời người dùng bấm Đồng ý
 * cho một câu họ chưa đọc, và họ không có đường nào biết LLM đã trích nhầm.
 *
 * 🔴 **KHÔNG khai `executorMayNoop`, có chủ đích** (khác `RECORD-PAID`) — nhưng LÝ DO không phải cái tôi viết vòng 1.
 * ⚠️ **Đính chính (review #863 MED)**: bản đầu khai *"khoá ledger dẫn từ `confirmation.id`"* — **SAI**, và cái sai đó
 * chính là TIỀN ĐỀ của quyết định bỏ cờ. Sự thật đo được ở `report-payment.service.ts:1008`: khoá dẫn từ **NỘI DUNG**
 * qua `deriveBusinessIdempotencyKey("self_payment_auto_confirm", memberDueId, {groupMemberId, amountMinor, currency,
 * paidAmountMinor, clientKey})` — cùng họ với `RECORD-PAID`.
 * Kết luận vẫn ĐÚNG, nhưng phải đứng trên lý do đúng: **cửa này không có nhánh replay-lặng**. Va khoá ⇒ `unique(fundId,
 * key)` ⇒ **409 fail-loud** (service tự ghi: *"vẫn gặp 409 khi hai lượt bằng nhau"*), chứ không trả nguyên kết quả cũ
 * như `createAdhoc`. Và lượt thứ hai trên CÙNG một suất không tới được đó: due đã rời tập khai-được
 * (`awaiting_confirmation`) ⇒ `_lockAndAssertReportable` ném 409 trước. Không có đường nào để executor "thành công mà
 * ghi 0 dòng" ⇒ khai cờ là tạo một câu `doneAlready` **không đường nào render tới** (câu chết).
 */
export const PAY_REPORT = {
  id: "PAY-REPORT",
  uc: "@UC0197",
  tier: "T2",
  status: LIVE,
  // Tự khai cho suất của CHÍNH MÌNH ⇒ mọi vai, kể cả member thường (khác RECORD-PAID chỉ O/A/T).
  roles: ALL_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    // Ô nhóm ĐẦU TIÊN: phạm vi ô `khoan` phụ thuộc nhóm đã chốt (`AI_SLOT_TYPE_NEEDS_GROUP.collection = true`),
    // và tab trợ lý là tab TOÀN CỤC — không khai ô này thì phiên scope "tất cả nhóm" trả 403 `GROUP_REQUIRED`.
    SLOT_GROUP,
    { ...SLOT_COLLECTION_AUTOFILL, id: "khoan", required: true, ask: "Bạn chuyển cho khoản nào? (đợt thu / khoản đang chờ)", clarify: "Bạn có mấy khoản đang chờ đóng — báo cho khoản nào?" },
    /*
     * 🔴 US-AIV2-tourguide-0010 (@UC0373, QĐ PO D1 01/10) — Ô CHỌN BÉ. Phụ huynh hai con cùng một đợt trước đây nhận `SEAT_AMBIGUOUS` ở
     * bước cuối (K09). Phạm vi ĐÓNG `represented` = suất nghĩa vụ CHÍNH người hỏi đại diện (`actorRepresentedLiveSeatWhere`) — tên bé
     * nhà khác không phân giải được. Tuỳ chọn, KHÔNG `autofill`: chỉ 1 suất khai được ⇒ binding tự chọn hàng duy nhất (không hỏi);
     * ≥2 ⇒ Người làm hỏi `ask` kèm nút từng bé (`slotCandidates` của binding ∩ phạm vi resolver — `binding-slot-offer.ts`).
     * Ô vào prompt (QĐ D1 chấp nhận thu lại eval) — sửa `ask`/thứ tự ô là làm mục bản ghi eval của MỌI ca có PAY-REPORT trong top-K.
     */
    { id: "be", type: "member", required: false, memberScope: "represented", ask: "Bạn báo cho bé nào? (tên bé / suất bạn đại diện)" },
    { id: "soTien", type: "money", required: false, ask: "Bạn đã chuyển bao nhiêu? (bỏ trống = đúng số còn phải đóng)" },
    // 🔴 review #863 HIGH-2 — VÍ DỤ KHÔNG ĐƯỢC MỜI GÕ SỐ TÀI KHOẢN. Bản cũ ghi *"ví dụ: chuyển từ tài khoản
    // của bố"*: đó là `ask` DUY NHẤT trong Sổ chứa chữ "tài khoản", và nó dẫn thẳng vào một đường KHÔNG XOÁ ĐƯỢC —
    // text tự do → `audit.input()` → `ai_audit_log.rawPrompt` lưu THÔ → bảng **append-only** (trigger
    // `ai_audit_log_no_mutate`/`no_truncate`) → `purge-user-pii` tự khai `rawPrompt = 0`, mà chủ số tài khoản
    // (bố/mẹ) thường KHÔNG có `userId` nên purge cũng không có đường tới. Số tài khoản là một trong BA loại PII
    // vẫn phải scrub — QĐ PO 18/09 chỉ nới TÊN NGƯỜI. Scrubber chặn nó tới LLM, nhưng không gỡ được nó khỏi sổ.
    // ⇒ giữ nguyên Ý ĐỊNH (nói hộ ai chuyển), bỏ LỜI MỜI nhập con số.
    { id: "ghiChu", type: "text", required: false, ask: "Ghi chú cho người giữ quỹ (ví dụ: bố chuyển hộ)?", textForm: "note" },
  ],
  llm: {
    description: "Người dùng báo rằng mình đã chuyển tiền cho một khoản phải đóng của chính mình; thủ quỹ đối chiếu sau.",
    examples: [
      "tôi vừa chuyển khoản đóng quỹ rồi",
      "toi vua chuyen khoan dong quy roi",
      "mình đã ck 200k tiền quỹ lớp",
      "minh da ck 200k",
      "báo đã đóng tiền đợt thu tháng 9",
      "bao da dong tien dot thu thang 9",
      "đã chuyển",
      "vừa chuyển 500k rồi nhé",
      "tôi đóng quỹ xong rồi, ghi nhận giúp",
      "đã nộp tiền quỹ",
      "chuyển rồi",
      "đã ck",
      // ——— 0105: thêm cho đủ 40 ———
      "da ck",
      "chuyen roi",
      "da nop tien quy",
      "vua chuyen 500k roi nhe",
      "toi dong quy xong roi ghi nhan giup",
      "mình vừa chuyển khoản xong",
      "minh vua chuyen khoan xong",
      "em đã chuyển tiền quỹ lớp rồi ạ",
      "em da chuyen tien quy lop roi a",
      "báo đã chuyển tiền",
      "bao da chuyen tien",
      "tôi chuyển đủ tiền đợt dã ngoại rồi",
      "toi chuyen du tien dot da ngoai roi",
      "vừa ck xong, báo nhóm giúp",
      "vua ck xong bao nhom giup",
      "mình đóng tiền rồi nhé",
      "minh dong tien roi nhe",
      "đã chuyển khoản cho quỹ nhóm",
      "da chuyen khoan cho quy nhom",
      "tôi nộp tiền mặt cho thủ quỹ rồi",
      "toi nop tien mat cho thu quy roi",
      "báo giúp mình đã chuyển 300k",
      "bao giup minh da chuyen 300k",
      "chuyển khoản xong rồi nha",
      "chuyen khoan xong roi nha",
      "mình trả phần của mình rồi",
      "minh tra phan cua minh roi",
      "tôi muốn báo đã đóng tiền",
      "toi muon bao da dong tien",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["có ai báo đã chuyển tiền chưa", "co ai bao da chuyen tien chua", "còn khoản nào của tôi chưa đóng không"],
  },
  /**
   * 🔴 US-AIV2-0110 S03 (@UC0260 · QĐ PO 4) — ĐÁP ÁN TIỀN của bộ eval, VIẾT TAY từ chính câu (không từ đầu ra model).
   * KHÔNG vào prompt ⇒ không đổi hash theo ca. Câu vắng mặt ở đây: không nói số tiền, hoặc mơ hồ bậc (§4).
   */
  evalAmounts: {
    "mình đã ck 200k tiền quỹ lớp": "200000",
    "minh da ck 200k": "200000",
    "vừa chuyển 500k rồi nhé": "500000",
    "vua chuyen 500k roi nhe": "500000",
    "báo giúp mình đã chuyển 300k": "300000",
    "bao giup minh da chuyen 300k": "300000",
  },
  executor: "reportPayment.reportPayment",
  /**
   * `@UC0216` — cửa mobile trang này chịu trách nhiệm: trạng thái (2) KHAI BÁO TRẢ của §3.7.
   *
   * US-E6.F3-0910 (@UC0057): + bước 1 upload BẰNG CHỨNG (ảnh/PDF) của chính việc nộp quỹ — bước phụ TUỲ CHỌN, chỉ có trên
   * màn nộp quỹ (chat không gửi được tệp). Không phải một việc riêng của người dùng ⇒ không dựng trang mới (trang mới đổi
   * tập trang của tầng tìm ⇒ ca eval thành "chưa đo"); `endpoints` không đi vào dấu vân tay eval.
   */
  endpoints: [
    "POST /member-dues/{memberDueId}/confirmations",
    "POST /funds/{fundId}/contribution-evidence",
    // US-E6.F3-0913 (@UC0057): member KHAI đóng góp "Quỹ chung" (không gắn kỳ) từ sheet nộp quỹ — CÙNG việc "báo đã chuyển" của trang này,
    // khác đích; lời khai LUÔN chờ O/A/T duyệt (G1). Chat chưa có binding cho đích này (khai cửa để bản đồ G14f kín, không mở việc mới).
    "POST /funds/{fundId}/contribution-claims",
    // US-E6.F3-0913 (@UC0057 · QĐ người dùng 07/10/2026 "Chỉ hiện suất của tôi"): bước chọn người của CHÍNH form khai Quỹ chung đọc các
    // suất người dùng khai được (CHỈ id, cùng vị-từ cửa khai). Cửa đọc, 0 ghi; chat không gọi (khai để bản đồ G14f kín).
    "GET /funds/{fundId}/contribution-claims/seats",
  ],
  facts: ["tenDot", "tenSuat", "soKhai", "soPhaiDong", "ghiChuGhi", "ketQua", "ketQuaThat"],
  templates: {
    // Thứ tự chỗ trống = thứ tự DÒNG PHIẾU (`renderConfirm` đọc `listPlaceholders`): nhóm → khoản → số khai → còn phải đóng → ghi chú → kết quả.
    // 🔴 KHÔNG có chữ "đối chiếu"/"chờ duyệt" nào ở đây — xem docblock: điều đó do cấu hình + vai quyết, và nó nằm ở `{ketQua}`.
    // 🔴 US-AIV2-0114 L05 (@UC0197): ghi chú là ô TUỲ CHỌN ⇒ đoạn `[[…]]` — bỏ trống thì cả vế ghi chú và dòng phiếu của nó biến mất (trước: "(không có)").
    // 🔴 @UC0373 — `{tenSuat}` trong ĐOẠN TUỲ CHỌN: phụ huynh nhiều bé phải ĐỌC tên bé trên phiếu trước khi bấm (AI-INV-4); nhóm thường 1 suất ⇒
    // fact rỗng ⇒ đoạn + dòng phiếu biến mất (0 regress câu chữ).
    confirm: "Ở nhóm {nhom}, bạn báo đã chuyển {soKhai} cho khoản “{tenDot}”[[ — suất của {tenSuat}]]. Suất này đang còn phải đóng {soPhaiDong}[[; ghi chú gửi người giữ quỹ: {ghiChuGhi}]]. {ketQua}",
    // 🔴 US-AIV2-0111 C06 — `{ketQuaThat}` (kết quả THẬT từ `execute`), KHÔNG `{ketQua}`: câu phiếu của lượt tự vào sổ mang ⚠️ dặn
    // TRƯỚC khi bấm; nhắc lại ⚠️ sau khi đã ghi là dọa người ta về việc đã xong.
    done: "Đã ghi lời báo của bạn: đã chuyển {soKhai} cho “{tenDot}”. {ketQuaThat}",
  },
  /** P5.3 — nhãn NGƯỜI ĐỌC cho từng dòng phiếu T2; thiếu một nhãn thì `renderConfirm` ném (không camelCase lên màn tiền). */
  labels: {
    nhom: "Nhóm",
    tenDot: "Khoản",
    tenSuat: "Cho suất",
    soKhai: "Bạn báo đã chuyển",
    soPhaiDong: "Đang còn phải đóng",
    ghiChuGhi: "Ghi chú cho người giữ quỹ",
    ketQua: "Sau khi bấm Đồng ý",
  },
  /**
   * US-AIV2-tourguide-0005 (@UC0364): bỏ `GROUP-BANK-ACCOUNT` («Chuyển tiền vào đâu») — vừa BÁO ĐÃ CHUYỂN xong thì việc chuyển đã làm (máy đo
   * K21: nút lạc đề sau việc xong).
   */
  journey: { outcomes: { done: { next: ["READ-PEOPLE-MINE"] } } },
  help: { title: "Báo tôi đã nộp tiền", tryNow: "tôi vừa chuyển khoản đóng quỹ rồi" },
} satisfies AiCapabilityPage;
